import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Network, Code, Play, TerminalSquare, Boxes, Cpu, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useSparcAgent } from '@/hooks/sparc/useSparcAgent';
import { toast } from 'sonner';
import AgentSpawner from './AgentSpawner';
import AgentInitializationState from './AgentInitializationState';

const AIAgents: React.FC = () => {
  const [activeTab, setActiveTab] = useState('spawner');
  const [swarmCode, setSwarmCode] = useState(`// Define a complex task that requires multiple agents
const task = {
  name: "Analyze and optimize learning module",
  description: "Perform comprehensive analysis and optimization of a learning module",
  subTasks: [
    {
      id: "content-analysis",
      name: "Content Analysis",
      assignedTo: "analyzer-agent"
    },
    {
      id: "structure-optimization",
      name: "Structure Optimization",
      dependsOn: ["content-analysis"],
      assignedTo: "architect-agent"
    },
    {
      id: "learning-path-integration",
      name: "Learning Path Integration",
      dependsOn: ["structure-optimization"],
      assignedTo: "integration-agent"
    }
  ]
};

// SPARC 2.0 swarm execution
sparc.executeSwarm(task);`);

  const [swarmOutput, setSwarmOutput] = useState<string | null>(null);
  const [swarmRunning, setSwarmRunning] = useState(false);
  
  const { loading, initialized, error, initAgent, execute } = useSparcAgent();

  // If not initialized, show loading or initialization button
  if (!initialized) {
    return <AgentInitializationState loading={loading} onInitialize={initAgent} />;
  }

  const handleRunSwarm = async () => {
    if (!swarmCode.trim()) {
      toast.error('Please enter some code to run');
      return;
    }

    setSwarmRunning(true);
    setSwarmOutput(null);

    try {
      // Execute the swarm code (this is a simulation)
      const jsCode = `
        // Simulate SPARC 2.0 swarm execution
        console.log("Initializing swarm execution environment...");
        
        // This is a simulation of what would happen in a real SPARC 2.0 environment
        function simulateSwarmExecution() {
          const output = [];
          
          output.push("[SPARC] Initializing swarm execution environment...");
          output.push("[SPARC] Parsing task definition and agent requirements...");
          output.push("[SPARC] Creating agent network with 3 specialized agents...");
          output.push("");
          output.push("[analyzer-agent] Starting content analysis...");
          output.push("[analyzer-agent] Examining learning objectives alignment...");
          output.push("[analyzer-agent] Analyzing content complexity metrics...");
          output.push("[analyzer-agent] Identifying knowledge gaps in current structure...");
          output.push("[analyzer-agent] Content analysis complete. Sending results to coordinator.");
          output.push("");
          output.push("[SPARC] Task 'content-analysis' completed. Starting 'structure-optimization'...");
          output.push("");
          output.push("[architect-agent] Receiving content analysis from analyzer-agent...");
          output.push("[architect-agent] Optimizing module structure based on analysis...");
          output.push("[architect-agent] Resequencing topics for improved knowledge building...");
          output.push("[architect-agent] Adding scaffolding elements at key complexity points...");
          output.push("[architect-agent] Structure optimization complete. Sending results to coordinator.");
          output.push("");
          output.push("[SPARC] Task 'structure-optimization' completed. Starting 'learning-path-integration'...");
          output.push("");
          output.push("[integration-agent] Receiving optimized structure from architect-agent...");
          output.push("[integration-agent] Integrating structure with global learning path...");
          output.push("[integration-agent] Updating prerequisites and postrequisites...");
          output.push("[integration-agent] Generating transition content between modules...");
          output.push("[integration-agent] Learning path integration complete.");
          output.push("");
          output.push("[SPARC] All tasks completed successfully.");
          output.push("[SPARC] Generated comprehensive module optimization with 3 agents working in coordination.");
          output.push("[SPARC] Total execution time: 4.2 seconds.");
          
          return output.join("\\n");
        }
        
        // Return the simulated output
        simulateSwarmExecution();
      `;

      // Execute the code
      const result = await execute(jsCode, 'javascript');
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      // Set the swarm output
      setSwarmOutput(result.result);
      toast.success('Swarm execution completed successfully');
      
    } catch (error) {
      console.error('Error running swarm:', error);
      toast.error(`Failed to run swarm: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSwarmOutput(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSwarmRunning(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Network className="mr-2 h-5 w-5 text-primary" />
          AI Agent Network
        </CardTitle>
        <CardDescription>
          Advanced AI agent capabilities powered by SPARC 2.0
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="spawner" className="flex items-center">
              <Cpu className="mr-2 h-4 w-4" />
              Agent Spawner
            </TabsTrigger>
            <TabsTrigger value="swarm" className="flex items-center">
              <Boxes className="mr-2 h-4 w-4" />
              Swarm Execution
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="spawner">
            <AgentSpawner />
          </TabsContent>
          
          <TabsContent value="swarm" className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium flex items-center">
                  <Code className="mr-2 h-5 w-5 text-primary" />
                  SPARC 2.0 Swarm Code
                </h3>
                
                <Button onClick={handleRunSwarm} disabled={swarmRunning || !swarmCode.trim()}>
                  {swarmRunning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Run Swarm
                    </>
                  )}
                </Button>
              </div>
              
              <Textarea 
                value={swarmCode}
                onChange={(e) => setSwarmCode(e.target.value)}
                className="font-mono text-sm"
                rows={10}
                disabled={swarmRunning}
              />
            </div>
            
            {swarmOutput && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium flex items-center">
                    <TerminalSquare className="mr-2 h-5 w-5 text-primary" />
                    Execution Output
                  </h3>
                  
                  <Badge variant="outline" className="bg-green-100 text-green-800">
                    Complete
                  </Badge>
                </div>
                
                <div className="bg-black text-green-400 p-4 rounded-md font-mono text-sm whitespace-pre-line overflow-auto max-h-[400px]">
                  {swarmOutput}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="border-t pt-4">
        <p className="text-sm text-muted-foreground">
          SPARC 2.0 enables advanced agent capabilities including agent self-replication and swarm intelligence for complex tasks.
        </p>
      </CardFooter>
    </Card>
  );
};

export default AIAgents;
